import { ChangeDetectorRef, Component, OnInit, OnDestroy, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';



// PrimeNG Modules
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { Popover } from 'primeng/popover';
import { FloatLabelModule } from 'primeng/floatlabel';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MessageModule } from 'primeng/message';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { TabsModule } from 'primeng/tabs';
import { BadgeModule } from 'primeng/badge';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// App Services
import { FirestoreService } from '../services/firestore';
import { DataService } from '../services/data';
import { NotificationService } from '../services/notification.service';
import { SpinnerService } from '../services/spinner.service';
import { Funciones } from '../services/funciones';
import { toJSDate } from '../helpers/fechas-helper';
import {
  HISTORIAL_UI_INITIAL,
  historialHasMore,
  nextHistorialVisibleCount,
} from '../utils/historial.util';

// Models
import { Offering } from '../models/offering.model';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { filter, take } from 'rxjs';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Auth } from '@angular/fire/auth';
// Firestore direct imports no longer needed; acceso a Firestore vía DataService/servicios
import { ToastService } from '../services/toast.service';
import { MovimientoOption, TIPO_OPTIONS } from '../constants/offering-options';
import { Member } from '../models/member.model';
import { PrimeNG } from 'primeng/config';
import { LanguageService } from '../services/language.service';
import { MissionCurrencyService } from '../services/mission-currency.service';
import { MissionMoneyPipe } from '../pipes/mission-money.pipe';
import { resolveCurrencyLocale } from '../utils/currency.util';
import {
  getMovementRegistrationBlockReason,
  PeriodosFinancierosState,
} from '../utils/periodo.util';

// Export libraries
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { ReportePdfService } from '../services/reporte-pdf.service';
import { MemberService } from '../services/member.service';
import { BankAccountService } from '../services/bank-account.service';
import { TipoMovimiento, CuentaBancaria, CUENTA_DEFAULT } from '../models/financial-config.model';

type TipoMovimientoLegacy = 'Ingreso' | 'Egreso';

interface HistorialCambioView {
  fecha: string;
  byName: string;
  action: 'create' | 'update';
  resumen?: string;
  campos?: string[];
}

interface AgregarMovimientoResponse {
  success?: boolean;
  ym?: string;
  periodoCreated?: boolean;
}

@Component({
  selector: 'app-offerings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    DatePickerModule,
    RadioButtonModule,
    FontAwesomeModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    Popover,
    FloatLabelModule,
    AutoCompleteModule,
    MessageModule,
    IconFieldModule,
    InputIconModule,
    TabsModule,
    BadgeModule,
    TranslateModule,
    MissionMoneyPipe,
  ],
  templateUrl: './offerings.html',
  styleUrls: ['./offerings.css'],
  encapsulation: ViewEncapsulation.None
})
export class Offerings implements OnInit, OnDestroy {

  @ViewChild('dt') dt!: Table;

  /** Moneda/locale para p-inputNumber (misma fuente que missionMoney). */
  get currencyCode(): string {
    return this.missionCurrency.getCurrencyCode();
  }

  get currencyLocale(): string {
    const lang = this.languageService.getCurrentLang() === 'it' ? 'it' : 'es';
    return resolveCurrencyLocale(this.currencyCode, lang);
  }

  /** Da Settings/features: export PDF/Excel in formato Prima nota (Italia) */
  habilitarPrimaNota = false;
  churchDisplayName = '';
  /** Saldi iniziali per cuenta desde primaNotaAperturas/{viewingYm} (tras cierre informe) */
  primaNotaAperturaPorCuenta: Record<string, number> | null = null;



  public rows: any[] = [];
  public filteredRows: any[] = [];
  public filterTotal: number = 0;
  public activeTabIndex: number = 0;
  private _showModal = false;
  public isEditing: boolean = false;

  get showModal(): boolean {
    return this._showModal;
  }

  set showModal(value: boolean) {
    if (value && !this.canOpenMovementModal(this.isEditing)) {
      return;
    }
    this._showModal = value;
  }

  globalFilterValue: string = '';

  /** Solo el pastor (y Admin) puede ver el historial de cambios de un movimiento. */
  public canViewLogs = false;
  public historialDialogVisible = false;
  public historialMovimiento: any | null = null;
  public historialEntries: HistorialCambioView[] = [];
  public historialVisibleCount = HISTORIAL_UI_INITIAL;

  public nuevaIglesia: boolean = false;
  public fechaCreacionIglesia: Date | null = null;
  private churchId: string | null = null;

  private subscriptions = new Subscription();
  private readonly missionCurrency = inject(MissionCurrencyService);
  private movimientosSubscription: Subscription | undefined;

  // Form Model
  newOffering: Offering = this.getInitialOffering();

  // Financial Summary
  public totalOfrendasMesActual: number = 0;
  public totalOfrendasDominicalesMesActual: number = 0;
  public totalOfrendasMisionerasMesActual: number = 0;
  public totalDiezmosMesActual: number = 0;
  public totalDiezmosDeDiezmosMesActual: number = 0;
  public totalSalidasMesActual: number = 0;
  public totalEntradasMesActual: number = 0;
  public totalEntradasRealesMesActual: number = 0;
  public totalProTemploMesActual: number = 0;
  public saldoDisponibleReal: number = 0;
  public previousMonthSummary: any = { saldo: 0 };
  public previousMonthName: string = '';
  monthName: string = '';
  /** YYYY-MM del mes cuyos movimientos se muestran (flechas + Consultar / Mes actual) */
  private viewingYm: string | null = null;
  puedeIrMesAnterior = false;
  puedeIrMesSiguiente = false;

  private readonly mesesNombreEs = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ] as const;

  saldoAnterior: number = 0;

  tipoOptionsMap: Record<'Ingreso' | 'Egreso', MovimientoOption[]> = { ...TIPO_OPTIONS };
  movimientoOptions: MovimientoOption[] = [
    {
      value: 'Ingreso', label: 'Ingreso',
      codigo: ''
    },
    {
      value: 'Egreso', label: 'Egreso',
      codigo: ''
    },
    {
      value: 'Transferencia', label: 'Transferencia',
      codigo: 'TRF-01'
    }
  ];

  private todosLosMiembros: any[] = [];
  opcionesMiembro: any[] = [];
  selectedMiembro: Member | null = null;
  miembroId: string | null = null;

  private originalOfferingForUpdate: any | null = null;

  monthDialog = false;
  selectedMonth: Date | null = null;
  libros: boolean = false;

  showBanner: boolean = false;
  movementsBlocked = false;
  openPeriodosCount = 0;
  openPeriodsDisplay = '';
  prevYM: string = '';
  periodoCerrado: boolean = false;

  maxMovementDate: Date = new Date();
  minMovementDate: Date = new Date(2020, 0, 1);
  maxMonthConsultDate: Date = new Date();

  private subsInitialized = false;
  searchNombre: string | Member | null = null;

  // Reporte Anual
  showReportDialog: boolean = false;
  selectedYearReport: string | null = null;
  yearsReport: any[] = [];
  generatingReport: boolean = false;

  // Gestión de Bancos (solo lectura - la administración está en Settings)
  cuentasBancarias: CuentaBancaria[] = [CUENTA_DEFAULT];
  tiposMovimiento: TipoMovimiento[] = [];
  manejarBancos: boolean = false; // Configuración de la iglesia para habilitar gestión de bancos

  // Saldos por cuenta bancaria (escucha en tiempo real)
  saldosCuentas: { [cuentaId: string]: number } = {};

  // Configuración de idioma español para DatePicker
  es = {
    firstDayOfWeek: 1,
    dayNames: ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'],
    dayNamesShort: ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    monthNamesShort: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    today: 'Hoy',
    clear: 'Limpiar'
  };

  private uiLang: 'es' | 'it' = 'es';

  constructor(
    private firestoreService: FirestoreService,
    private dataService: DataService,
    private notificationService: NotificationService,
    private spinnerService: SpinnerService,
    private funciones: Funciones,
    private confirmationService: ConfirmationService,
    private toastService: ToastService,
    private config: PrimeNG,
    private router: Router,
    private memberService: MemberService,
    private reportePdfService: ReportePdfService,
    private bankAccountService: BankAccountService, // mantenido para posibles operaciones CRUD futura de cuentas
    private auth: Auth,
    private languageService: LanguageService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    // Inicializar años para el reporte (desde 2024 hacia atrás)
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= 2020; i--) {
      this.yearsReport.push({ label: i.toString(), value: i.toString() });
    }
  }

  ngOnInit(): void {
    this.refreshMovementDateBounds();

    // Check Permissions
    this.subscriptions.add(
      this.dataService.userRoles$.subscribe(roles => {
        const allowedRoles = ['Admin', 'Pastor', 'Tesorería', 'access_finanzas'];
        const hasAccess = roles.some(role => allowedRoles.includes(role));
        if (!hasAccess) {
          this.router.navigate(['/dashboard/main']);
        }
        this.canViewLogs = roles.some(role => ['Pastor', 'Admin'].includes(role));
      })
    );

    // Configurar idioma español para DatePicker
    this.config.setTranslation(this.es);

    const churchIdSubscription = this.dataService.churchId$.subscribe(churchId => {
      if (this.movimientosSubscription) {
        this.movimientosSubscription.unsubscribe();
      }

      if (churchId) {
        const switchedChurch = this.churchId !== null && this.churchId !== churchId;
        if (this.churchId && this.churchId !== churchId) {
          this.rows = [];
          this.filteredRows = [];
          this.recalculateTotals(); // Pone todo en 0
          this.selectedMonth = null;
          this.viewingYm = null;
        }
        this.churchId = churchId;
        if (switchedChurch) {
          this.mesActual();
        }

        // Las configuraciones financieras (cuentas, saldos, tipos) se cargan
        // automáticamente desde DataService al inicializar con el churchId.
      } else {
        this.churchId = null;
        this.viewingYm = null;
        this.updateMonthNavAvailability();
        this.rows = [];
        this.filteredRows = [];
        this.recalculateTotals();
      }
    });
    this.subscriptions.add(churchIdSubscription);
    // ✅ instalar listeners a DataService solo una vez
    if (!this.subsInitialized) {
      this.suscripciones();
      this.subsInitialized = true;
    }

    // ✅ Si viewingYm no está sincronizado (recarga con monthName cacheado), cargar mes actual
    if (!this.viewingYm) {
      this.mesActual();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.movimientosSubscription) {
      this.movimientosSubscription.unsubscribe();
    }
  }

  applyGlobalFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.globalFilterValue = filterValue;
    if (this.dt) {
      this.dt.filterGlobal(filterValue, 'contains');
    }
  }

  public financialCards: any[] = [];

  private updateFinancialCards(): void {
    const diezmos = Number(this.totalDiezmosMesActual || 0);
    const diezmosDeDiezmos = Number(this.totalDiezmosDeDiezmosMesActual || 0);
    const ofrendas = Number(this.totalOfrendasMesActual || 0);
    const ofrendasDominicales = Number(this.totalOfrendasDominicalesMesActual || 0);
    const ofrendaMisionera = Number(this.totalOfrendasMisionerasMesActual || 0);
    const proTemplo = Number(this.totalProTemploMesActual || 0);

    this.financialCards = [
      {
        label: this.translate.instant('FINANCE.CARD_TITHES_CHURCH'),
        value: diezmos,
        icon: 'pi pi-percentage',
        color: 'blue'
      },
      {
        label: this.translate.instant('FINANCE.CARD_TITHES_OF_TITHES'),
        value: diezmosDeDiezmos,
        icon: 'pi pi-chart-pie',
        color: 'indigo'
      },
      {
        label: this.translate.instant('FINANCE.CARD_DAILY_OFFERINGS'),
        value: ofrendas,
        icon: 'pi pi-wallet',
        color: 'cyan'
      },
      {
        label: this.translate.instant('FINANCE.CARD_SUNDAY_SCHOOL_OFFERINGS'),
        value: ofrendasDominicales,
        icon: 'pi pi-book',
        color: 'teal'
      },
      {
        label: this.translate.instant('FINANCE.CARD_MISSION_OFFERING'),
        value: ofrendaMisionera,
        icon: 'pi pi-globe',
        color: 'bluegray'
      },
      {
        label: this.translate.instant('FINANCE.CARD_SPECIAL_OFFERING'),
        value: proTemplo,
        icon: 'pi pi-star', // Changed icon to star as building/temple is less relevant now
        color: 'purple'
      }
    ].filter(card => card.value > 0);
  }

  private lookupCodigo(
    tipo: 'Ingreso' | 'Egreso' | null,
    concepto: string | null,
    isNewChurch: boolean = this.nuevaIglesia // pásalo explícito si prefieres
  ): string | null {
    if (!tipo || !concepto) return null;
    const options = this.tipoOptionsMap[tipo] ?? [];
    return options.find(o => o.value === concepto)?.codigo ?? null;
  }

  private suscripciones() {

    // Cargar config dinámica de items desde DataService (Firestore) o usar defaults locales
    this.subscriptions.add(
      this.dataService.offeringConfig$.subscribe(config => {
        this.tipoOptionsMap = config;
      })
    );

    // Re-render de labels de conceptos cuando cambia idioma UI
    this.subscriptions.add(
      this.languageService.currentLang$.subscribe(lang => {
        this.uiLang = (lang === 'it' ? 'it' : 'es');
        this.updateFinancialCards();
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.dataService.offerings$
        .pipe(filter(ofs => Array.isArray(ofs)))
        .subscribe(offerings => {
          this.rows = [...offerings]; // ← Crear nueva referencia para forzar detección de cambios
          this.filterRowsByTab();
          this.recalculateTotals();
        })
    );

    this.subscriptions.add(
      this.dataService.previousMonthSummary$.subscribe(summary => {
        this.previousMonthSummary = summary || { saldo: 0, ofrendas: 0, diezmos: 0, salidas: 0, totalEntradas: 0 };
        this.recalculateRealTotalDisponible();
      })
    );

    this.subscriptions.add(
      this.dataService.openPeriodo$.subscribe(periodo => {
        const ym = periodo?.id || '';
        const name = this.funciones.formatYMtoText(ym);
        this.previousMonthName = name;
      })
    );

    this.subscriptions.add(
      this.dataService.monthName$.subscribe(name => {
        this.monthName = name;
        if (name && !this.viewingYm) {
          const ym = this.parseSpanishMonthLabelToYm(name);
          if (ym) {
            this.viewingYm = ym;
            this.updateMonthNavAvailability();
          }
        }
      })
    );

    this.subscriptions.add(
      this.dataService.members$.subscribe(members => {
        this.todosLosMiembros = members || [];
      })
    );

    this.subscriptions.add(
      this.dataService.banner$.subscribe(show => {
        this.showBanner = show;
      })
    );

    this.subscriptions.add(
      this.dataService.periodosFinancierosState$.subscribe(state => {
        this.movementsBlocked = state.movementsBlocked;
        this.openPeriodosCount = state.count;
        this.openPeriodsDisplay = this.formatOpenPeriodsList(state);
        this.refreshMovementDateBounds();
        this.updateMonthNavAvailability();
        this.cdr.markForCheck();
      })
    );

    this.subscriptions.add(
      this.dataService.dataChurch$.subscribe(data => {
        this.fechaCreacionIglesia = data?.fechaCreacion ? data.fechaCreacion : null;
        this.nuevaIglesia = data?.startNew || false;
        this.churchDisplayName = data?.churchName || '';
        if (this.nuevaIglesia) {
          this.newOffering = this.getInitialOffering();
        }
        this.updateMonthNavAvailability();
      })
    );

    this.subscriptions.add(
      this.dataService.habilitarPrimaNota$.subscribe(v => {
        this.habilitarPrimaNota = v;
        void this.refreshPrimaNotaApertura();
      })
    );

    this.subscriptions.add(
      this.dataService.manejarBancos$.subscribe(value => {
        this.manejarBancos = value;
      })
    );

    this.subscriptions.add(
      this.dataService.periodoCerrado$.subscribe(periodo => {
        this.periodoCerrado = periodo;
      })
    );

    this.subscriptions.add(
      this.dataService.librosOfferings$.subscribe(libros => {
        this.libros = libros;
      })
    );

    // Sync cuentas bancarias desde DataService (cargadas al login si tiene acceso a finanzas)
    this.subscriptions.add(
      this.dataService.cuentasBancarias$.subscribe(cuentas => {
        this.cuentasBancarias = cuentas;
      })
    );

    // Sync saldos de cuentas en tiempo real desde DataService
    this.subscriptions.add(
      this.dataService.saldosCuentas$.subscribe(saldos => {
        this.saldosCuentas = saldos;
      })
    );

    // Sync tipos de movimiento activos desde DataService
    this.subscriptions.add(
      this.dataService.tiposMovimiento$.subscribe(tipos => {
        this.tiposMovimiento = tipos;
        this.movimientoOptions = tipos.map(tipo => ({
          value: tipo.value,
          label: tipo.label,
          codigo: ''
        }));
      })
    );

  }

  openDialogLibros() {
    this.selectedMonth = null;
    this.monthDialog = true;
  }

  consultarMovimientos() {
    const month = (this.selectedMonth?.getMonth() ?? 0) + 1;
    const year = this.selectedMonth?.getFullYear();
    this.monthDialog = false;
    this.dataService.updateLibrosOfferings(true);
    this.loadFinancialData(this.churchId!, month, year);
  }

  mesActual() {
    this.selectedMonth = null;
    this.dataService.updateLibrosOfferings(false);
    this.saldoAnterior = 0;
    this.loadFinancialData(this.churchId!);
  }

  navegarMesRelativo(delta: number): void {
    if (!this.churchId || !this.viewingYm) return;
    const [yStr, mStr] = this.viewingYm.split('-');
    const y0 = parseInt(yStr, 10);
    const m0 = parseInt(mStr, 10);
    if (!Number.isFinite(y0) || !Number.isFinite(m0)) return;
    const d = new Date(y0, m0 - 1 + delta, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const nextYm = `${y}-${String(m).padStart(2, '0')}`;
    const minYm = this.getMinYm();
    const maxYm = this.getMaxYm();
    if (nextYm < minYm || nextYm > maxYm) return;
    if (nextYm === maxYm) {
      this.mesActual();
    } else {
      this.dataService.updateLibrosOfferings(true);
      this.loadFinancialData(this.churchId, m, y);
    }
  }

  buscarMiembros(event: any) {
    const query = event.query.toLowerCase();

    this.opcionesMiembro = this.todosLosMiembros
      .filter(member =>
        (`${member.nombres} ${member.apellidos || ''}`.toLowerCase().includes(query))
      )
      .map(member => ({
        ...member,
        nombreCompleto: `${member.nombres} ${member.apellidos || ''}`,
        id: member.id
      }));
  }

  seleccionarMiembro(member: Member | null): void {
    if (!member) {
      this.selectedMiembro = null;
      this.miembroId = null;
      this.newOffering.nombre = '';
      return;
    }
    this.selectedMiembro = member;
    this.miembroId = (member as any).id ?? null;
    this.searchNombre = member.nombres + ' ' + (member.apellidos || '');
    this.newOffering.nombre = member.nombres + ' ' + (member.apellidos || '');
  }

  onNombreChange(value: any): void {
    // Si el autocomplete devuelve un objeto, tratar como selección
    if (value && typeof value === 'object' && 'nombreCompleto' in value) {
      this.seleccionarMiembro(value as Member);
      return;
    }

    // Si es string (escritura libre) o vacío -> quitar selección de miembro
    const txt = value ? String(value) : '';
    this.selectedMiembro = null;
    this.miembroId = null;
    this.newOffering.nombre = txt;
    this.searchNombre = txt;
  }

  /**
   * Mes/año mostrado en la franja bajo el header (según `viewingYm` y el idioma UI).
   */
  get monthStripDisplay(): string {
    if (!this.viewingYm) return '';
    const [ys, ms] = this.viewingYm.split('-');
    const y = parseInt(ys, 10);
    const m = parseInt(ms, 10);
    if (Number.isFinite(y) && Number.isFinite(m) && m >= 1 && m <= 12) {
      const locale = this.languageService.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
      return new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    }
    return '';
  }

  get movimientoOptionsFiltered(): MovimientoOption[] {
    const base = this.manejarBancos
      ? this.movimientoOptions
      : this.movimientoOptions.filter(o => o.value !== 'Transferencia');

    return base.map(option => ({
      ...option,
      label: this.getMovementTypeLabelByValue(option.value, option.label)
    }));
  }

  getMovementTypeLabelByValue(value: string | null | undefined, fallback: string = ''): string {
    switch (value) {
      case 'Ingreso':
        return this.translate.instant('FINANCE.MOVEMENT_TYPE_INCOME');
      case 'Egreso':
        return this.translate.instant('FINANCE.MOVEMENT_TYPE_EXPENSE');
      case 'Transferencia':
        return this.translate.instant('FINANCE.MOVEMENT_TYPE_TRANSFER');
      default:
        return fallback || (value ?? '');
    }
  }

  getConceptLabelByRow(row: any): string {
    const fallback = String(row?.concepto ?? '').trim();
    const codigo = String(row?.codigo ?? '').trim();
    if (!fallback) return '';
    return this.languageService.getItemLabel(codigo, fallback);
  }

  private getViewingYmLastDay(): Date | null {
    if (!this.viewingYm) return null;
    const [ys, ms] = this.viewingYm.split('-');
    const y = parseInt(ys, 10);
    const m = parseInt(ms, 10);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
    // Day 0 of next month = last day of current month (local time).
    return new Date(y, m, 0);
  }

  private getPrimaNotaAggregatedLabel(codigo: string, sampleRow?: any): string {
    const code = String(codigo ?? '').trim();
    if (sampleRow) {
      const label = this.getConceptLabelByRow(sampleRow);
      if (label) return label;
    }
    return this.languageService.getItemLabel(code, code) || code;
  }

  private shouldAggregateContributoInPrimaNota(row: any): boolean {
    // Goal: group month totals per contributo code, without member names.
    // Default rule: income codes starting with ING- excluding tithes and balance rows.
    if (String(row?.tipoMovimiento ?? '').trim() !== 'Ingreso') return false;
    const codigoRaw = String(row?.codigo ?? '').trim().toUpperCase();
    const codigo = codigoRaw || String(this.lookupCodigo('Ingreso', row?.concepto ?? null) ?? '').trim().toUpperCase();
    if (!codigo.startsWith('ING-')) return false;
    if (codigo === 'ING-SI') return false; // saldo inicial
    if (codigo === 'ING-SAL-ANT') return false; // saldo mes anterior / cierre informe
    return true;
  }

  get currentTipoOptions(): MovimientoOption[] {
    const mov = this.newOffering.tipoMovimiento as 'Ingreso' | 'Egreso';
    /*     if (mov === 'Ingreso' && !this.nuevaIglesia) {
          // filtrar ING-SI si no es nueva iglesia, excepto si estamos editando un movimiento que ya lo tiene
          if (this.isEditing && this.originalOfferingForUpdate?.codigo === 'ING-SI') {
            return this.tipoOptionsMap[mov];
          }
          //return this.tipoOptionsMap[mov].filter(o => o.codigo !== 'ING-SI');
        } */
    const base = mov ? (this.tipoOptionsMap[mov] ?? []) : [];

    // value se mantiene en ES (persistencia / lookup); label se traduce solo para UI
    if (this.uiLang !== 'it') return base;

    return base.map(o => ({
      ...o,
      label: this.languageService.getItemLabel(o.codigo, o.label),
    }));
  }

  onConceptoChange(concepto: string | null) {
    this.newOffering.concepto = concepto;
    this.newOffering.codigo = this.lookupCodigo(this.newOffering.tipoMovimiento as any, concepto);
  }

  private loadFinancialData(churchId: string, month?: number, year?: number): void {
    if (!churchId) {
      return;
    }

    this.spinnerService.show();

    if (this.movimientosSubscription) {
      this.movimientosSubscription.unsubscribe();
      this.movimientosSubscription = undefined;
    }

    // 1) Fecha base: params -> selectedMonth -> hoy
    const base = (Number.isInteger(year) && Number.isInteger(month))
      ? new Date(year!, (month as number) - 1, 1)
      : (this.selectedMonth instanceof Date && !isNaN(this.selectedMonth.valueOf()))
        ? new Date(this.selectedMonth.getFullYear(), this.selectedMonth.getMonth(), 1)
        : (() => { const now = new Date(); return new Date(now.getFullYear(), now.getMonth(), 1); })();


    // 2) Año/mes actuales (para getMovimientos)
    const currentYear = String(base.getFullYear());
    const currentMonth = String(base.getMonth() + 1).padStart(2, '0'); // 01..12

    this.viewingYm = `${base.getFullYear()}-${currentMonth}`;
    this.updateMonthNavAvailability();

    // 3) Actualizar el nombre del mes en el header para que coincida con los datos mostrados
    const nameMonth = this.obtenerNombreMesYAno(currentMonth, currentYear);
    this.dataService.updateMonthName(nameMonth);

    // 4) Movimientos del mes base
    try {
      this.movimientosSubscription = this.firestoreService
        .getMovimientos(churchId, currentYear, currentMonth)
        .subscribe({
          next: movimientos => this.dataService.updateOfferings(movimientos),
          error: () => {
            if (this.churchId) this.notificationService.showError('Error al cargar movimientos.');
          }
        });
    } catch (error) {

    }
    finally {
      this.spinnerService.hide();
    }

    void this.refreshPrimaNotaApertura();
  }

  /** Carga snapshot Prima nota para el mes visualizado (solo si habilitarPrimaNota). */
  private async refreshPrimaNotaApertura(): Promise<void> {
    if (!this.habilitarPrimaNota || !this.churchId || !this.viewingYm) {
      this.primaNotaAperturaPorCuenta = null;
      this.cdr.markForCheck();
      return;
    }
    const map = await this.dataService.fetchPrimaNotaApertura(this.churchId, this.viewingYm);
    this.primaNotaAperturaPorCuenta = map;
    this.cdr.markForCheck();
  }

  private recalculateTotals(): void {
    let entradas = 0,
      salidas = 0,
      ofrendas = 0,
      ofrendasED = 0,
      diezmos = 0,
      ofrendaMisionera = 0,
      entradasDiezmos = 0,
      proTemplo = 0;

    this.saldoAnterior = 0; // Resetear saldo anterior para evitar datos stale


    this.rows.forEach(mov => {
      const monto = mov.monto || 0;

      if (mov.tipoMovimiento === 'Ingreso') {

        // ✅ excluir ambos saldos de las entradas normales
        if (mov.concepto !== 'Saldo Inicial' && mov.concepto !== 'Saldo Mes Anterior') {
          entradas += monto;
        }

        if (mov.codigo === 'ING-02') {
          ofrendas += monto;
          entradasDiezmos += monto;
        }
        if (mov.codigo === 'ING-03') {
          ofrendasED += monto;
          entradasDiezmos += monto;
        }
        if (mov.codigo === 'ING-01') {
          diezmos += monto;
          entradasDiezmos += monto;
        }
        if (mov.concepto === 'Ofrenda Misionera') ofrendaMisionera += monto;
        if (mov.codigo === 'ING-07') proTemplo += monto;

        // Guardar saldo anterior/inicial (no sumarlo)
        if (mov.concepto === 'Saldo Inicial' || mov.concepto === 'Saldo Mes Anterior') {
          this.saldoAnterior = monto;
        }

      } else if (mov.tipoMovimiento === 'Egreso') {
        salidas += monto;
      }
    });

    this.totalEntradasMesActual = entradas;
    this.totalOfrendasDominicalesMesActual = ofrendasED;
    this.totalSalidasMesActual = salidas;
    this.totalOfrendasMesActual = ofrendas;
    this.totalDiezmosMesActual = diezmos;
    this.totalOfrendasMisionerasMesActual = ofrendaMisionera;
    this.totalDiezmosDeDiezmosMesActual = entradasDiezmos * 0.1; // 10% de los diezmos
    this.totalEntradasRealesMesActual = entradasDiezmos;
    this.totalProTemploMesActual = proTemplo;

    this.recalculateRealTotalDisponible();
    this.updateFinancialCards();
  }

  private recalculateRealTotalDisponible(): void {
    const saldoInicial = this.saldoAnterior;
    this.saldoDisponibleReal = saldoInicial + this.totalEntradasMesActual - this.totalSalidasMesActual;
    this.dataService.updateSaldoDisponibleReal(this.saldoDisponibleReal);
  }

  private getInitialOffering(): Offering {
    const cuentaDefault = this.cuentasBancarias?.find(c => c.esDefault)?.id || 'caja-menor';

    if (this.nuevaIglesia) {
      return {
        tipoMovimiento: 'Ingreso',
        monto: null,
        fecha: new Date(),
        metodo: 'Efectivo',
        concepto: 'Saldo Inicial',
        codigo: 'ING-SI',
        nombre: '',
        cuentaDestino: cuentaDefault, // Pre-seleccionar cuenta por defecto
        cuentaOrigen: null,
      };
    } else {
      return {
        tipoMovimiento: 'Ingreso',
        monto: null,
        fecha: new Date(),
        metodo: 'Efectivo',
        concepto: '',
        nombre: '',
        cuentaDestino: cuentaDefault, // Pre-seleccionar cuenta por defecto
        cuentaOrigen: null,
      };
    }
  }

  openAddModal(): void {
    if (!this.canOpenMovementModal(false)) return;
    this.isEditing = false;
    this.newOffering = this.getInitialOffering();
    this.showModal = true;
  }

  /** YM usado para validar alta (mes visualizado o mes calendario actual). */
  private getYmForMovementValidation(): string {
    return this.viewingYm ?? this.getMaxYm();
  }

  /**
   * Única puerta para abrir el diálogo de movimiento (salvo edición / iglesia nueva).
   * Devuelve false si el periodo destino está bloqueado.
   */
  private canOpenMovementModal(forEdit: boolean): boolean {
    if (forEdit || this.nuevaIglesia) return true;
    const block = this.getMovementBlockReasonForYm(this.getYmForMovementValidation());
    if (block) {
      this.showMovementBlockToast(block);
      return false;
    }
    return true;
  }

  private showMovementBlockToast(block: 'future' | 'backlog' | 'before_first'): void {
    const key =
      block === 'future'
        ? 'FINANCE.ERR_FUTURE_MONTH'
        : block === 'before_first'
          ? 'FINANCE.ERR_BEFORE_FIRST_PERIOD'
          : 'FINANCE.ERR_MOVEMENTS_BLOCKED';
    this.toastService.showError(this.translate.instant(key));
  }

  /** Respaldo si el diálogo se intenta mostrar por otro camino (p. ej. two-way binding). */
  guardMovementDialogOnShow(): void {
    if (this.isEditing || this.nuevaIglesia) return;
    if (!this.canOpenMovementModal(false)) {
      this._showModal = false;
      this.cdr.detectChanges();
    }
  }

  get isAddMovementDisabled(): boolean {
    if (this.nuevaIglesia) return false;
    return this.getMovementBlockReasonForYm(this.getYmForMovementValidation()) !== null;
  }

  /** Motivo del bloqueo de "Nuevo Movimiento" para el recorrido guiado (`data-tour-blocked*`). */
  get addMovementBlock(): { text: string; route: string | null; action: string | null } | null {
    if (this.nuevaIglesia) return null;
    const block = this.getMovementBlockReasonForYm(this.getYmForMovementValidation());
    if (!block) return null;
    if (block === 'backlog') {
      return {
        text: this.translate.instant('FINANCE.TOUR_BLOCK_BACKLOG', { periods: this.openPeriodsDisplay || '—' }),
        route: '/dashboard/reports',
        action: this.translate.instant('FINANCE.TOUR_BLOCK_GO_REPORTS'),
      };
    }
    const key = block === 'future' ? 'FINANCE.TOUR_BLOCK_FUTURE' : 'FINANCE.TOUR_BLOCK_BEFORE_FIRST';
    return { text: this.translate.instant(key), route: null, action: null };
  }

  private formatOpenPeriodsList(state: PeriodosFinancierosState): string {
    return state.openYms.map(ym => this.funciones.formatYMtoText(ym)).join(', ');
  }

  private refreshMovementDateBounds(): void {
    const maxFirst = this.getMaxViewingDateFirstOfMonth();
    this.maxMovementDate = new Date(maxFirst.getFullYear(), maxFirst.getMonth() + 1, 0);
    this.maxMonthConsultDate = this.maxMovementDate;
    this.minMovementDate = this.getMinViewingDateFirstOfMonth();
  }

  private getMovementBlockReasonForYm(targetYm: string) {
    const state = this.dataService.getPeriodosFinancierosState();
    return getMovementRegistrationBlockReason(targetYm, state, this.getMaxYm());
  }

  editOffering(row: any): void {
    this.isEditing = true;

    // Guarda los valores del doc tal cual (como están en Firestore / en el path)
    this.originalOfferingForUpdate = {
      ...row,
      prevYear: row.year,   // <- del registro cargado
      prevMonth: row.month, // <- del registro cargado (si es '09', que siga '09')
    };

    const cuentaDefault = this.cuentasBancarias?.find(c => c.esDefault)?.id || 'caja-menor';

    this.newOffering = {
      ...row,
      fecha: row.fecha?.toDate ? row.fecha.toDate() : new Date(row.fecha),
      codigo: row.codigo ?? this.lookupCodigo(row.tipoMovimiento, row.concepto),
      // Retrocompatibilidad: asignar cuentas por defecto si no existen
      cuentaOrigen: row.cuentaOrigen || (row.tipoMovimiento === 'Egreso' || row.tipoMovimiento === 'Transferencia' ? cuentaDefault : null),
      cuentaDestino: row.cuentaDestino || (row.tipoMovimiento === 'Ingreso' || row.tipoMovimiento === 'Transferencia' ? cuentaDefault : null),
    };

    this.showModal = true;
  }

  async deleteOffering(offering: any): Promise<void> {
    const functions = getFunctions();
    this.confirmationService.confirm({
      message: `¿Deseas eliminar este movimiento?`,
      header: 'Confirmar Eliminar Movimiento',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: async () => {
        try {
          this.spinnerService.show();
          const deleteOfferingsCallable = httpsCallable(functions, 'deleteMovimiento');
          await deleteOfferingsCallable({ churchId: this.churchId, movimiento: offering });
          this.toastService.showSuccess('Movimiento eliminado correctamente');
          this.spinnerService.hide();
        } catch (error) {
          this.toastService.showError('Hubo un error al eliminar la tarea');
          this.spinnerService.hide();
        }
      }
    });
  }

  async saveMovimiento(): Promise<void> {
    if (!this.churchId) {
      this.notificationService.showError('No se pudo guardar, ID de iglesia no disponible.');
      return;
    }
    this.spinnerService.show();

    try {
      const functions = getFunctions();

      const fechaAsDate: Date = toJSDate(this.newOffering.fecha);

      // ✅ FIX CRÍTICO: Normalizar a mediodía LOCAL para evitar cambios de día por timezone
      // Usar setHours(12) asegura que si es 1-Feb 00:00 (que puede ser 31-Ene UTC),
      // pase a 1-Feb 12:00 Local (que sigue siendo 1-Feb UTC).
      // Usar setUTCHours(12) era un error porque fijaba el día en el "ayer" si ya había cambiado.
      fechaAsDate.setHours(12, 0, 0, 0);

      const month = fechaAsDate.getMonth() + 1;
      const year = fechaAsDate.getFullYear();
      const targetYm = `${year}-${String(month).padStart(2, '0')}`;
      const blockReason = this.getMovementBlockReasonForYm(targetYm);
      if (blockReason) {
        this.showMovementBlockToast(blockReason);
        this.spinnerService.hide();
        return;
      }

      // Validacion para iglesias nuevas: Saldo Inicial debe ser >= 2026
      if (this.nuevaIglesia && year < 2026) {
        this.toastService.showError('El Saldo Inicial debe ser a partir del 1 de Enero de 2026');
        this.spinnerService.hide();
        return;
      }

      let codigo = this.lookupCodigo(this.newOffering.tipoMovimiento as any, this.newOffering.concepto ?? null);

      // ====== VALIDACIÓN Y ASIGNACIÓN DE CUENTAS ======
      const cuentaDefault = this.cuentasBancarias?.find(c => c.esDefault)?.id || 'caja-menor';

      // Para Transferencias: validar que haya ambas cuentas y que sean diferentes
      if (this.newOffering.tipoMovimiento === 'Transferencia') {
        if (!this.newOffering.cuentaOrigen || !this.newOffering.cuentaDestino) {
          this.toastService.showError('Para una transferencia debe seleccionar cuenta origen y cuenta destino');
          this.spinnerService.hide();
          return;
        }
        if (this.newOffering.cuentaOrigen === this.newOffering.cuentaDestino) {
          this.toastService.showError('La cuenta origen y destino no pueden ser la misma');
          this.spinnerService.hide();
          return;
        }
        // Asignar concepto y código fijos para Transferencias (no tienen sub-concepto)
        this.newOffering.concepto = 'Transferencia';
        codigo = 'TRF-01';
      }

      // Para Ingresos: asignar cuenta destino (default si no está seleccionada)
      if (this.newOffering.tipoMovimiento === 'Ingreso') {
        if (!this.newOffering.cuentaDestino) {
          this.newOffering.cuentaDestino = cuentaDefault;
        }
        // Limpiar cuenta origen para Ingresos
        this.newOffering.cuentaOrigen = null;
      }

      // Para Egresos: asignar cuenta origen (default si no está seleccionada)
      if (this.newOffering.tipoMovimiento === 'Egreso') {
        if (!this.newOffering.cuentaOrigen) {
          this.newOffering.cuentaOrigen = cuentaDefault;
        }
        // Limpiar cuenta destino para Egresos
        this.newOffering.cuentaDestino = null;
      }
      // ====== FIN VALIDACIÓN CUENTAS ======

      // ====== VALIDACIÓN DE FONDOS DISPONIBLES ======
      if (this.newOffering.tipoMovimiento === 'Egreso' || this.newOffering.tipoMovimiento === 'Transferencia') {
        if (this.manejarBancos) {
          const origenId = this.newOffering.cuentaOrigen;
          const montoRequerido = Number(this.newOffering.monto) || 0;
          let saldoDisponible = origenId ? (this.saldosCuentas[origenId] || 0) : 0;
          
          if (this.isEditing && this.originalOfferingForUpdate && this.originalOfferingForUpdate.cuentaOrigen === origenId) {
            // Si estamos editando y es la misma cuenta origen, sumamos su valor original al disponible 
            // ya que formaba parte del saldo actual y será reemplazado
            saldoDisponible += Number(this.originalOfferingForUpdate.monto || 0);
          }

          if (montoRequerido > saldoDisponible) {
            const cuentaOrigenName = this.cuentasBancarias?.find(c => c.id === origenId)?.nombre || 'la cuenta origen';
            this.toastService.showError(`Fondos insuficientes. Solo hay ${this.missionCurrency.format(saldoDisponible)} disponible(s) en ${cuentaOrigenName}.`);
            this.spinnerService.hide();
            return;
          }
        }
      }
      // ====== FIN VALIDACIÓN DE FONDOS DISPONIBLES ======

      const isDiezmo =
        this.newOffering.tipoMovimiento === 'Ingreso' &&
        codigo === 'ING-01';

      let nombreToSave = '';
      if (this.selectedMiembro) {
        nombreToSave = `${this.selectedMiembro.nombres ?? ''} ${this.selectedMiembro.apellidos ?? ''}`.trim();
      } else if (typeof this.searchNombre === 'string' && this.searchNombre.trim().length > 0) {
        nombreToSave = this.searchNombre.trim();
      } else if (this.newOffering && this.newOffering.nombre) {
        nombreToSave = String(this.newOffering.nombre).trim();
      }

      this.newOffering.nombre = nombreToSave;

      const miembroNombreSnapshot = isDiezmo
        ? (this.selectedMiembro
          ? `${this.selectedMiembro.nombres ?? ''} ${this.selectedMiembro.apellidos ?? ''}`.trim()
          : (this.newOffering.nombre ?? '').trim())
        : null;

      let sanitizedOffering: any;

      if (this.isEditing) {
        sanitizedOffering = {
          ...this.newOffering,
          codigo,
          fecha: fechaAsDate,
          month,
          year,
        };

        const prevYear = String(this.originalOfferingForUpdate?.prevYear ?? this.originalOfferingForUpdate?.year ?? '');
        const prevMonth = String(this.originalOfferingForUpdate?.prevMonth ?? this.originalOfferingForUpdate?.month ?? '');

        const updateOfferingsCallable = httpsCallable<
          { churchId: string; movimiento: any; prevYear?: string; prevMonth?: string },
          AgregarMovimientoResponse
        >(functions, 'updateMovimiento');

        const callableRes = await updateOfferingsCallable({
          churchId: this.churchId!,
          movimiento: sanitizedOffering,
          prevYear,
          prevMonth,
        });

        const resData = (callableRes?.data ?? {}) as AgregarMovimientoResponse;

        if (resData?.success) {
          const needsRefresh = !!resData.periodoCreated || !!resData.ym;
          if (needsRefresh) {
            try {
              const periodo = await firstValueFrom(this.firestoreService.getOldestOpenPeriodo(this.churchId!));
              this.dataService.updateOpenPeriodo(periodo);
            } catch (err) {
            }
          }
          this.toastService.showSuccess('Movimiento actualizado correctamente');

          // Si el movimiento es diezmo, limpiar variables relacionadas con la selección
          if (isDiezmo) {
            this.selectedMiembro = null;
            this.miembroId = null;
            this.searchNombre = '';
            this.newOffering.nombre = '';
          }
        }
      } else {
        if (isDiezmo) {
          sanitizedOffering = {
            ...this.newOffering,
            codigo,
            fecha: fechaAsDate,
            ...(this.miembroId ? { idMiembro: this.miembroId } : {}),
            nombre: nombreToSave,
            month,
            year
          };
        } else {
          // evitar propagar nombre/idMiembro a movimientos que no son diezmos
          const basePayload: any = { ...this.newOffering };
          delete basePayload.nombre;
          if ('idMiembro' in basePayload) delete basePayload.idMiembro;

          sanitizedOffering = {
            ...basePayload,
            codigo,
            fecha: fechaAsDate,
            month,
            year
          };
        }

        const saveOfferingsCallable = httpsCallable<{ churchId: string; movimiento: any }, AgregarMovimientoResponse>(functions, 'agregarMovimiento');
        const callableRes = await saveOfferingsCallable({
          churchId: this.churchId,
          movimiento: sanitizedOffering,
        });

        const resData = (callableRes && (callableRes as any).data) as AgregarMovimientoResponse;

        if (resData?.success) {
          const needsRefresh = !!resData.periodoCreated || !!resData.ym;
          if (needsRefresh) {
            try {
              const periodo = await firstValueFrom(this.firestoreService.getOldestOpenPeriodo(this.churchId));
              this.dataService.updateOpenPeriodo(periodo);
            } catch (err) {
            }
          }

          if (this.nuevaIglesia) {
            const updateStartNew = httpsCallable(functions, 'updateStartNew');
            await updateStartNew({
              churchId: this.churchId,
              startNew: false
            });
          }
          this.toastService.showSuccess('Movimiento registrado correctamente');

          // limpiar variables al crear un diezmo
          if (isDiezmo) {
            this.selectedMiembro = null;
            this.miembroId = null;
            this.searchNombre = '';
            this.newOffering.nombre = '';
          }
        }
      }

      // Limpieza UI general (no borrar aquí lo específico de Diezmo porque ya lo manejamos)
      this.showModal = false;
      this.originalOfferingForUpdate = null;
    } catch (error: any) {
      this.toastService.showError(error?.message || 'Error al guardar el movimiento');
    } finally {
      // Aseguramos spinner off y no sobreescribimos otras variables útiles
      this.spinnerService.hide();
    }
  }

  // Método para manejar cambio de pestaña
  selectTab(index: number) {
    this.activeTabIndex = index;
    this.filterRowsByTab();
  }

  onTabChange(event: any) {
    const newIndex = event.value !== undefined ? event.value : event;
    this.activeTabIndex = newIndex;
    this.filterRowsByTab();
  }

  // Filtrado por pestañas
  filterRowsByTab() {
    const index = this.activeTabIndex;

    if (index === 0) {
      // Todo
      this.filteredRows = [...this.rows];
      this.filterTotal = this.filteredRows.reduce((sum, row) => sum + (row.monto || 0), 0);
    } else if (index === 1) {
      // Ingresos
      this.filteredRows = this.rows.filter(row => row.tipoMovimiento === 'Ingreso');
      this.filterTotal = this.filteredRows.reduce((sum, row) => sum + (row.monto || 0), 0);
    } else if (index === 2) {
      // Egresos
      this.filteredRows = this.rows.filter(row => row.tipoMovimiento === 'Egreso');
      this.filterTotal = this.filteredRows.reduce((sum, row) => sum + (row.monto || 0), 0);
    } else if (index === 3) {
      // Diezmos
      this.filteredRows = this.rows.filter(row =>
        row.codigo === 'ING-01'
      );
      this.filterTotal = this.filteredRows.reduce((sum, row) => sum + (row.monto || 0), 0);
    } else if (index === 4) {
      // Ofrendas
      this.filteredRows = this.rows.filter(row =>
        row.codigo?.toLowerCase().includes('ing-02') ||
        row.codigo?.toLowerCase().includes('ing-03') ||
        row.codigo?.toLowerCase().includes('ing-04') ||
        row.codigo?.toLowerCase().includes('ing-05') ||
        row.codigo?.toLowerCase().includes('ing-06') ||
        row.codigo?.toLowerCase().includes('ing-07') ||
        row.codigo?.toLowerCase().includes('ing-08')
      );
      this.filterTotal = this.filteredRows.reduce((sum, row) => sum + (row.monto || 0), 0);
    } else if (index === 5) {
      // Ofrenda Especial (Pro-Templo)
      this.filteredRows = this.rows.filter(row =>
        row.codigo?.toLowerCase().includes('ing-07')
      );
      this.filterTotal = this.filteredRows.reduce((sum, row) => sum + (row.monto || 0), 0);
    } else {
      this.filteredRows = [...this.rows];
      this.filterTotal = this.filteredRows.reduce((sum, row) => sum + (row.monto || 0), 0);
    }
  }

  convertTimestampString(date: any): string {
    if (!date) return '';
    const jsDate = date.toDate ? date.toDate() : new Date(date);
    return this.funciones.convertirFechaHoraMinutos(jsDate, false);
  }

  /**
   * Abre el diálogo con el historial de cambios embebido en el movimiento.
   * Solo accesible para Pastor/Admin (gobernado por canViewLogs en la plantilla).
   */
  openHistorial(rowData: any): void {
    this.historialMovimiento = rowData;
    const entries: any[] = Array.isArray(rowData?.historialCambios)
      ? rowData.historialCambios
      : [];

    this.historialEntries = entries
      .map((entry) => ({
        fecha: this.convertTimestampString(entry?.at),
        byName: entry?.byName || rowData?.updatedByName || rowData?.createdByName || '—',
        action: entry?.action === 'create' ? 'create' : 'update',
        resumen: entry?.resumen || undefined,
        campos: Array.isArray(entry?.campos) ? entry.campos : undefined,
      } as HistorialCambioView))
      .reverse();

    this.historialVisibleCount = HISTORIAL_UI_INITIAL;
    this.historialDialogVisible = true;
  }

  get visibleHistorialEntries(): HistorialCambioView[] {
    return this.historialEntries.slice(0, this.historialVisibleCount);
  }

  get hasMoreHistorialEntries(): boolean {
    return historialHasMore(this.historialVisibleCount, this.historialEntries.length);
  }

  loadMoreHistorial(): void {
    this.historialVisibleCount = nextHistorialVisibleCount(
      this.historialVisibleCount,
      this.historialEntries.length
    );
  }

  closeHistorial(): void {
    this.historialDialogVisible = false;
    this.historialMovimiento = null;
    this.historialEntries = [];
    this.historialVisibleCount = HISTORIAL_UI_INITIAL;
  }

  getHistorialMovimientoTitulo(): string {
    const row = this.historialMovimiento;
    if (!row) return '';
    const concepto = this.getConceptLabelByRow(row) || row?.concepto || '';
    return concepto ? `${concepto}` : '';
  }

  /**
   * Obtiene el nombre de una cuenta dado su ID.
   * Para retrocompatibilidad, si no hay ID, devuelve 'Caja' (cuenta por defecto).
   */
  getCuentaNombre(cuentaId: string | null | undefined): string {
    if (!cuentaId) return 'Caja';
    const cuenta = this.cuentasBancarias?.find(c => c.id === cuentaId);
    return cuenta?.nombre || 'Caja';
  }

  /**
   * Obtiene solo las cuentas activas para mostrar en los dropdowns.
   */
  getCuentasActivas(): CuentaBancaria[] {
    return this.cuentasBancarias.filter(c => c.activo);
  }

  /**
   * Obtiene las cuentas activas excluyendo la cuenta origen seleccionada.
   * Usado en transferencias para evitar seleccionar la misma cuenta como origen y destino.
   */
  getCuentasActivasExceptoOrigen(): CuentaBancaria[] {
    if (!this.newOffering.cuentaOrigen) {
      return this.cuentasBancarias.filter(c => c.activo);
    }
    return this.cuentasBancarias.filter(c => c.activo && c.id !== this.newOffering.cuentaOrigen);
  }

  obtenerNombreMesYAno(month: string, year: string): string {
    // Convertir a números y validar
    const mesNumero = parseInt(month, 10);
    const anioNumero = parseInt(year, 10);

    // Validaciones básicas
    if (isNaN(mesNumero) || mesNumero < 1 || mesNumero > 12) {
      throw new Error('Mes inválido. Debe ser entre 01 y 12');
    }

    if (isNaN(anioNumero) || anioNumero < 1000 || anioNumero > 9999) {
      throw new Error('Año inválido. Debe ser un año de 4 dígitos');
    }

    // Obtener nombre del mes (restamos 1 porque el array empieza en 0)
    const nombreMes = this.mesesNombreEs[mesNumero - 1];

    return `${nombreMes}  ${anioNumero}`;
  }

  /** Parsea etiquetas tipo "Abril  2026" (nombres en español) a YYYY-MM */
  private parseSpanishMonthLabelToYm(label: string): string | null {
    if (!label?.trim()) return null;
    const parts = label.trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return null;
    const yearStr = parts[parts.length - 1];
    if (!/^\d{4}$/.test(yearStr)) return null;
    const monthPart = parts.slice(0, -1).join(' ');
    const idx = this.mesesNombreEs.findIndex(m => m.toLowerCase() === monthPart.toLowerCase());
    if (idx < 0) return null;
    const y = parseInt(yearStr, 10);
    return `${y}-${String(idx + 1).padStart(2, '0')}`;
  }

  private dateToYm(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }

  private getMinViewingDateFirstOfMonth(): Date {
    const minYm = this.dataService.getPeriodosFinancierosState().minPeriodYm;
    if (minYm) {
      const [yStr, mStr] = minYm.split('-');
      const y = parseInt(yStr, 10);
      const m = parseInt(mStr, 10);
      if (Number.isFinite(y) && Number.isFinite(m)) {
        return new Date(y, m - 1, 1);
      }
    }
    const raw = this.fechaCreacionIglesia as unknown;
    if (raw) {
      try {
        const d = toJSDate(raw);
        if (!isNaN(d.getTime())) {
          return new Date(d.getFullYear(), d.getMonth(), 1);
        }
      } catch {
        /* ignorar */
      }
    }
    return new Date(2020, 0, 1);
  }

  private getMaxViewingDateFirstOfMonth(): Date {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  }

  private getMinYm(): string {
    const state = this.dataService.getPeriodosFinancierosState();
    if (state.minPeriodYm) return state.minPeriodYm;
    const fromCreation = this.dateToYm(this.getMinViewingDateFirstOfMonth());
    const oldest = state.oldestYm;
    if (oldest && oldest < fromCreation) return oldest;
    return fromCreation;
  }

  private getMaxYm(): string {
    return this.dateToYm(this.getMaxViewingDateFirstOfMonth());
  }

  private updateMonthNavAvailability(): void {
    if (!this.viewingYm) {
      this.puedeIrMesAnterior = false;
      this.puedeIrMesSiguiente = false;
      return;
    }
    const minYm = this.getMinYm();
    const maxYm = this.getMaxYm();
    this.puedeIrMesAnterior = this.viewingYm > minYm;
    this.puedeIrMesSiguiente = this.viewingYm < maxYm;
  }

  exportToExcel() {
    if (this.habilitarPrimaNota) {
      this.exportPrimaNotaExcel();
      return;
    }
    try {
      // Preparar datos
      const data = this.prepareAccountingData();

      // Crear workbook
      const wb = XLSX.utils.book_new();

      // Crear hoja con los datos
      const ws = XLSX.utils.aoa_to_sheet(data);

      // Ajustar anchos de columna
      ws['!cols'] = [
        { wch: 12 }, // Fecha
        { wch: 40 }, // Descripción
        { wch: 15 }, // Debe
        { wch: 15 }, // Haber
        { wch: 15 }  // Saldo
      ];

      // Agregar hoja al workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Ofrendas');

      // Generar archivo
      const tabName = this.getTabName();
      const fileName = `Relacion_${tabName}_${this.monthStripDisplay.replace(/\s+/g, '_')}.xlsx`;
      XLSX.writeFile(wb, fileName);

      this.toastService.showSuccess('Excel exportado correctamente');
    } catch (error) {
      this.toastService.showError('Error al exportar Excel');
    }
  }

  exportToPdf() {
    if (this.habilitarPrimaNota) {
      this.exportPrimaNotaPdf();
      return;
    }
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();

      // Logo a la izquierda
      const logoPath = '../../../assets/images/logo_mmm.png';
      try {
        doc.addImage(logoPath, 'PNG', 15, 10, 25, 25);
      } catch (e) {
      }

      // Título centrado
      const tabName = this.getTabName();
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(`RELACION ${tabName.toUpperCase()}`, pageWidth / 2, 20, { align: 'center' });

      // Mes y Año centrado debajo del título
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(this.monthStripDisplay.toUpperCase(), pageWidth / 2, 28, { align: 'center' });

      // Preparar datos para la tabla
      const tableData = this.prepareTableData();

      // Crear tabla
      autoTable(doc, {
        startY: 40,
        head: [['Fecha', 'Descripción', 'Debe', 'Haber', 'Saldo']],
        body: tableData.rows,
        foot: tableData.totals,
        theme: 'grid',
        styles: {
          fontSize: 9,
          cellPadding: 3,
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center'
        },
        footStyles: {
          fillColor: [236, 240, 241],
          textColor: 0,
          fontStyle: 'bold'
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 25 },
          1: { halign: 'left', cellWidth: 80 },
          2: { halign: 'right', cellWidth: 25 },
          3: { halign: 'right', cellWidth: 25 },
          4: { halign: 'right', cellWidth: 25 }
        },
        didParseCell: (data) => {
          // Resaltar filas de totales
          if (data.row.section === 'body') {
            const rowData = data.row.raw as any[];
            if (rowData && rowData[1] && rowData[1].toString().startsWith('TOTAL:')) {
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.fillColor = [236, 240, 241];
            }
          }
        }
      });

      // Guardar PDF
      const fileName = `Relacion_${tabName}_${this.monthStripDisplay.replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);

      this.toastService.showSuccess('PDF exportado correctamente');
    } catch (error) {
      this.toastService.showError('Error al exportar PDF');
    }
  }

  /** Periodo en la cabecera del PDF/Excel (mes según idioma UI, como la franja del mes). */
  private getPrimaNotaPeriodLine(): string {
    if (!this.viewingYm) return '';
    const [ys, ms] = this.viewingYm.split('-');
    const y = parseInt(ys, 10);
    const m = parseInt(ms, 10);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return '';
    const locale = this.languageService.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
    const mes = new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long' });
    return `${mes.toUpperCase()} - ${y}`;
  }

  private formatPrimaNotaCurrency(value: number): string {
    const uiLang = this.languageService.getCurrentLang() === 'it' ? 'it' : 'es';
    return this.missionCurrency.format(value, { uiLang });
  }

  private primaNotaDateLocale(): string {
    return this.languageService.getCurrentLang() === 'it' ? 'it-IT' : 'es-ES';
  }

  /** Cabeceras tabla Prima nota (PDF/Excel) alineadas al idioma activo. */
  private getPrimaNotaExportHeaders(): string[] {
    const up = (key: string) => this.translate.instant(key).toUpperCase();
    return [
      up('FINANCE.COL_DATE'),
      up('FINANCE.COL_CONCEPT'),
      up('FINANCE.PRIMA_NOTA_COL_IN'),
      up('FINANCE.PRIMA_NOTA_COL_OUT'),
      up('FINANCE.PRIMA_NOTA_COL_BALANCE_SHORT')
    ];
  }

  private defaultCuentaId(): string {
    return this.cuentasBancarias?.find(c => c.esDefault)?.id || CUENTA_DEFAULT.id;
  }

  private getPrimaNotaLibros(): CuentaBancaria[] {
    const activas = (this.cuentasBancarias || []).filter(c => c.activo);
    if (activas.length === 0) return [{ ...CUENTA_DEFAULT }];
    return activas.sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
  }

  private primaNotaTitoloLibro(c: CuentaBancaria): string {
    if (c.tipo === 'efectivo') {
      return this.translate.instant('FINANCE.PRIMA_NOTA_TITLE_CASH');
    }
    const raw = (c.numeroCuenta || c.iban || '').replace(/\s/g, '');
    const tail = raw.length >= 5 ? raw.slice(-5) : '';
    if (tail) {
      return this.translate.instant('FINANCE.PRIMA_NOTA_TITLE_BANK_CC', { tail });
    }
    return this.translate.instant('FINANCE.PRIMA_NOTA_TITLE_BANK', { name: c.nombre });
  }

  private entrataUscitaForAccount(row: any, cuentaId: string): { entrata: number; uscita: number } {
    const monto = Number(row.monto || 0);
    const tipo = String(row.tipoMovimiento || '');
    const def = this.defaultCuentaId();

    if (tipo === 'Transferencia') {
      const orig = row.cuentaOrigen as string | null | undefined;
      const dest = row.cuentaDestino as string | null | undefined;
      let entrata = 0;
      let uscita = 0;
      if (orig === cuentaId) uscita = monto;
      if (dest === cuentaId) entrata = monto;
      return { entrata, uscita };
    }
    if (tipo === 'Ingreso') {
      const dest = (row.cuentaDestino as string | undefined) || def;
      return dest === cuentaId ? { entrata: monto, uscita: 0 } : { entrata: 0, uscita: 0 };
    }
    if (tipo === 'Egreso') {
      const orig = (row.cuentaOrigen as string | undefined) || def;
      return orig === cuentaId ? { entrata: 0, uscita: monto } : { entrata: 0, uscita: 0 };
    }
    return { entrata: 0, uscita: 0 };
  }

  private computeNetMonthForAccount(cuentaId: string): number {
    let net = 0;
    for (const row of this.rows) {
      const { entrata, uscita } = this.entrataUscitaForAccount(row, cuentaId);
      net += entrata - uscita;
    }
    return net;
  }

  /**
   * Saldo iniziale per libro: primero snapshot tras cierre informe (primaNotaAperturas),
   * si no hay y el mes es el actual, derivar de saldosCuentas live.
   */
  private getOpeningBalanceForAccount(cuentaId: string): number | null {
    if (this.primaNotaAperturaPorCuenta && typeof this.primaNotaAperturaPorCuenta[cuentaId] === 'number') {
      return this.primaNotaAperturaPorCuenta[cuentaId];
    }
    if (this.viewingYm !== this.getMaxYm()) return null;
    const cur = this.saldosCuentas[cuentaId] ?? 0;
    return cur - this.computeNetMonthForAccount(cuentaId);
  }

  private rowTouchesAccount(row: any, cuentaId: string): boolean {
    const { entrata, uscita } = this.entrataUscitaForAccount(row, cuentaId);
    return entrata > 0 || uscita > 0;
  }

  /**
   * Misma lógica que la columna "Concepto" en offerings.html (salvo pestaña solo-diezmos que solo muestra nombre).
   */
  private describeMovementPrimaNota(row: any): string {
    const label = this.getConceptLabelByRow(row);
    const nombreRaw = row.nombre;
    const nombre =
      nombreRaw != null && String(nombreRaw).trim() !== '' ? String(nombreRaw).trim() : '';
    const descripcionRaw = row.descripcion;
    const descripcion =
      descripcionRaw != null && String(descripcionRaw).trim() !== '' ? String(descripcionRaw).trim() : '';
    const codigoUpper = String(row.codigo || '').trim().toUpperCase();

    if (nombre) {
      return `${label} - (${nombre})`;
    }
    if (row.autoGenerated === true && codigoUpper === 'ING-SAL-ANT') {
      const sub = label || '-';
      return `${this.translate.instant('FINANCE.CLOSE_REPORT_LABEL')} - (${sub})`;
    }
    if (descripcion) {
      return `${label} ( ${descripcion})`;
    }
    return label || '';
  }

  private buildPrimaNotaBodyRows(cuenta: CuentaBancaria): {
    body: string[][];
    totEntrata: number;
    totUscita: number;
    hasOpening: boolean;
    openingSaldo: number;
  } {
    const sorted = [...this.rows]
      .filter(r => this.rowTouchesAccount(r, cuenta.id))
      .sort((a, b) => {
        const dateA = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
        const dateB = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
        return dateA.getTime() - dateB.getTime();
      });

    const opening = this.getOpeningBalanceForAccount(cuenta.id);
    let saldo = opening !== null ? opening : 0;
    const body: string[][] = [];
    let totEntrata = 0;
    let totUscita = 0;

    if (opening !== null) {
      body.push([
        '',
        this.translate.instant('FINANCE.PRIMA_NOTA_OPENING_ROW'),
        '',
        '',
        this.formatPrimaNotaCurrency(saldo)
      ]);
    }

    const aggregated = new Map<string, { entrata: number; uscita: number; sampleRow: any }>();

    for (const row of sorted) {
      const { entrata, uscita } = this.entrataUscitaForAccount(row, cuenta.id);
      if (entrata === 0 && uscita === 0) continue;

      if (this.shouldAggregateContributoInPrimaNota(row)) {
        const codigoRaw = String(row?.codigo ?? '').trim().toUpperCase();
        const codigoFallback = String(this.lookupCodigo('Ingreso', row?.concepto ?? null) ?? '').trim().toUpperCase();
        const codigo = codigoRaw || codigoFallback;
        if (codigo) {
          const cur = aggregated.get(codigo) ?? { entrata: 0, uscita: 0, sampleRow: row };
          cur.entrata += entrata;
          cur.uscita += uscita;
          if (!cur.sampleRow) cur.sampleRow = row;
          aggregated.set(codigo, cur);
          continue;
        }
      }

      // Fila detallada (sin agrupar)
      totEntrata += entrata;
      totUscita += uscita;
      saldo += entrata - uscita;
      const fecha = row.fecha?.toDate ? row.fecha.toDate() : new Date(row.fecha);
      const fechaStr = fecha.toLocaleDateString(this.primaNotaDateLocale(), { day: '2-digit', month: '2-digit', year: '2-digit' });
      body.push([
        fechaStr,
        this.describeMovementPrimaNota(row),
        entrata > 0 ? this.formatPrimaNotaCurrency(entrata) : '',
        uscita > 0 ? this.formatPrimaNotaCurrency(uscita) : '',
        this.formatPrimaNotaCurrency(saldo)
      ]);
    }

    // Líneas agregadas por código (total del mes, sin nombre de miembro)
    const lastDay = this.getViewingYmLastDay();
    const aggFechaStr = lastDay
      ? lastDay.toLocaleDateString(this.primaNotaDateLocale(), { day: '2-digit', month: '2-digit', year: '2-digit' })
      : '';

    for (const codigo of Array.from(aggregated.keys()).sort((a, b) => a.localeCompare(b))) {
      const bucket = aggregated.get(codigo);
      if (!bucket) continue;
      const aIn = bucket.entrata;
      const aOut = bucket.uscita;
      if (aIn === 0 && aOut === 0) continue;

      totEntrata += aIn;
      totUscita += aOut;
      saldo += aIn - aOut;

      body.push([
        aggFechaStr,
        this.getPrimaNotaAggregatedLabel(codigo, bucket.sampleRow),
        aIn > 0 ? this.formatPrimaNotaCurrency(aIn) : '',
        aOut > 0 ? this.formatPrimaNotaCurrency(aOut) : '',
        this.formatPrimaNotaCurrency(saldo)
      ]);
    }

    body.push(['', '', '', '', '']);
    body.push([
      '',
      this.translate.instant('FINANCE.PRIMA_NOTA_TOTALS_ROW'),
      this.formatPrimaNotaCurrency(totEntrata),
      this.formatPrimaNotaCurrency(totUscita),
      ''
    ]);

    return {
      body,
      totEntrata,
      totUscita,
      hasOpening: opening !== null,
      openingSaldo: opening ?? 0
    };
  }

  private sheetNameSanitize(name: string): string {
    const s = name.replace(/[[\]:\\/?*]/g, '-').trim();
    return (s.slice(0, 31) || 'Libro');
  }

  private exportPrimaNotaExcel(): void {
    try {
      const wb = XLSX.utils.book_new();
      const libros = this.getPrimaNotaLibros();
      const periodo = this.getPrimaNotaPeriodLine();
      const nomeChiesa = this.churchDisplayName || this.translate.instant('FINANCE.PRIMA_NOTA_CHURCH_FALLBACK');
      let sheets = 0;

      for (const cuenta of libros) {
        const { body } = this.buildPrimaNotaBodyRows(cuenta);
        if (body.length <= 2) continue;

        const titolo = this.primaNotaTitoloLibro(cuenta);
        const aoa: any[][] = [
          [nomeChiesa],
          [periodo],
          [titolo],
          [],
          this.getPrimaNotaExportHeaders(),
          ...body
        ];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = [{ wch: 12 }, { wch: 44 }, { wch: 16 }, { wch: 16 }, { wch: 16 }];
        XLSX.utils.book_append_sheet(wb, ws, this.sheetNameSanitize(cuenta.nombre || cuenta.id));
        sheets++;
      }

      if (sheets === 0) {
        this.toastService.showWarning(this.translate.instant('FINANCE.PRIMA_NOTA_EXPORT_EMPTY'));
        return;
      }

      const safePeriod = periodo.replace(/\s+/g, '_');
      XLSX.writeFile(wb, `Prima_nota_${safePeriod}.xlsx`);
      this.toastService.showSuccess(this.translate.instant('FINANCE.PRIMA_NOTA_EXPORT_OK_EXCEL'));
    } catch {
      this.toastService.showError(this.translate.instant('FINANCE.PRIMA_NOTA_EXPORT_ERR_EXCEL'));
    }
  }

  private exportPrimaNotaPdf(): void {
    try {
      const libros = this.getPrimaNotaLibros();
      const periodo = this.getPrimaNotaPeriodLine();
      const nomeChiesa = this.churchDisplayName || this.translate.instant('FINANCE.PRIMA_NOTA_CHURCH_FALLBACK');
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      let firstPage = true;
      let anyPage = false;

      for (const cuenta of libros) {
        const { body } = this.buildPrimaNotaBodyRows(cuenta);
        if (body.length <= 2) continue;

        if (!firstPage) doc.addPage();
        firstPage = false;
        anyPage = true;

        try {
          doc.addImage('../../../assets/images/logo_mmm.png', 'PNG', 15, 10, 25, 25);
        } catch { /* logo opcional */ }

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(nomeChiesa.toUpperCase(), pageWidth / 2, 18, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(periodo, pageWidth / 2, 25, { align: 'center' });
        doc.setFont('helvetica', 'bold');
        doc.text(this.primaNotaTitoloLibro(cuenta), pageWidth / 2, 32, { align: 'center' });

        autoTable(doc, {
          startY: 38,
          head: [this.getPrimaNotaExportHeaders()],
          body,
          theme: 'grid',
          styles: { fontSize: 8, cellPadding: 2 },
          headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold', halign: 'center' },
          columnStyles: {
            0: { halign: 'center', cellWidth: 22 },
            1: { halign: 'left', cellWidth: 78 },
            2: { halign: 'right', cellWidth: 26 },
            3: { halign: 'right', cellWidth: 26 },
            4: { halign: 'right', cellWidth: 26 }
          }
        });
      }

      if (!anyPage) {
        this.toastService.showWarning(this.translate.instant('FINANCE.PRIMA_NOTA_EXPORT_EMPTY'));
        return;
      }

      const safePeriod = periodo.replace(/\s+/g, '_');
      doc.save(`Prima_nota_${safePeriod}.pdf`);
      this.toastService.showSuccess(this.translate.instant('FINANCE.PRIMA_NOTA_EXPORT_OK_PDF'));
    } catch {
      this.toastService.showError(this.translate.instant('FINANCE.PRIMA_NOTA_EXPORT_ERR_PDF'));
    }
  }

  private prepareAccountingData(): any[][] {
    // Header con título dinámico
    const tabName = this.getTabName();
    const data: any[][] = [
      [`RELACION ${tabName.toUpperCase()}`],
      [this.monthStripDisplay.toUpperCase()],
      [],
      ['Fecha', 'Descripción', 'Debe', 'Haber', 'Saldo']
    ];

    // Ordenar por fecha
    const sortedRows = [...this.filteredRows].sort((a, b) => {
      const dateA = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
      const dateB = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
      return dateA.getTime() - dateB.getTime();
    });

    // Calcular saldo corrido
    let saldo = 0;

    sortedRows.forEach(row => {
      const fecha = row.fecha?.toDate ? row.fecha.toDate() : new Date(row.fecha);
      const fechaStr = fecha.toLocaleDateString('es-ES');
      const monto = Number(row.monto || 0);

      const debe = row.tipoMovimiento === 'Ingreso' ? monto : 0;
      const haber = row.tipoMovimiento === 'Egreso' ? monto : 0;

      saldo += debe - haber;

      // Obtener descripción según el tipo
      const descripcion = this.getDescripcionForExport(row);

      data.push([
        fechaStr,
        descripcion,
        debe > 0 ? debe.toFixed(2) : '',
        haber > 0 ? haber.toFixed(2) : '',
        saldo.toFixed(2)
      ]);
    });

    // Agregar fila de TOTAL con el saldo final
    data.push([]);
    data.push([
      '',
      'TOTAL',
      '',
      '',
      saldo.toFixed(2)
    ]);

    return data;
  }

  private prepareTableData(): { rows: any[][], totals: any[][] } {
    // Ordenar por fecha
    const sortedRows = [...this.filteredRows].sort((a, b) => {
      const dateA = a.fecha?.toDate ? a.fecha.toDate() : new Date(a.fecha);
      const dateB = b.fecha?.toDate ? b.fecha.toDate() : new Date(b.fecha);
      return dateA.getTime() - dateB.getTime();
    });

    // Calcular saldo corrido
    let saldo = 0;
    const rows: any[][] = [];

    sortedRows.forEach(row => {
      const fecha = row.fecha?.toDate ? row.fecha.toDate() : new Date(row.fecha);
      const fechaStr = fecha.toLocaleDateString('es-ES');
      const monto = Number(row.monto || 0);

      const debe = row.tipoMovimiento === 'Ingreso' ? monto : 0;
      const haber = row.tipoMovimiento === 'Egreso' ? monto : 0;

      saldo += debe - haber;

      // Obtener descripción según el tipo
      const descripcion = this.getDescripcionForExport(row);

      rows.push([
        fechaStr,
        descripcion,
        debe > 0 ? this.missionCurrency.format(debe) : '',
        haber > 0 ? this.missionCurrency.format(haber) : '',
        this.missionCurrency.format(saldo)
      ]);
    });

    // Agregar fila de TOTAL con el saldo final
    rows.push(['', '', '', '', '']);
    rows.push([
      '',
      'TOTAL',
      '',
      '',
      this.missionCurrency.format(saldo)
    ]);

    return { rows, totals: [] };
  }

  private calculateTotalsByConcepto(rows: any[]): any[] {
    const totales = new Map<string, { debe: number, haber: number }>();

    rows.forEach(row => {
      const concepto = row.concepto || 'Sin concepto';
      const monto = Number(row.monto || 0);

      if (!totales.has(concepto)) {
        totales.set(concepto, { debe: 0, haber: 0 });
      }

      const total = totales.get(concepto)!;
      if (row.tipoMovimiento === 'Ingreso') {
        total.debe += monto;
      } else {
        total.haber += monto;
      }
    });

    // Convertir a array y ordenar
    return Array.from(totales.entries())
      .map(([concepto, { debe, haber }]) => ({ concepto, debe, haber }))
      .sort((a, b) => (b.debe + b.haber) - (a.debe + a.haber));
  }

  private getTabName(): string {
    const tabNames = ['Movimientos', 'Ingresos', 'Egresos', 'Diezmos', 'Ofrendas', 'Ofrenda Especial'];
    return tabNames[this.activeTabIndex] || 'Movimientos';
  }

  private getDescripcionForExport(row: any): string {
    // Para Diezmos (tab 3), usar solo el campo 'nombre'
    if (this.activeTabIndex === 3 && row.nombre) {
      return String(row.nombre).trim();
    }

    // Para otros casos, usar concepto + descripcion + nombre (si aplica)
    const concepto = row.concepto ? String(row.concepto).trim() : '';
    const descripcion = row.descripcion ? String(row.descripcion).trim() : '';
    const nombre = row.nombre ? String(row.nombre).trim() : '';

    // Para pestañas Todo (0) e Ingresos (1), incluir nombre si existe
    const incluirNombre = (this.activeTabIndex === 0 || this.activeTabIndex === 1) && nombre;

    // Construir descripción combinada
    let resultado = concepto;

    if (descripcion) {
      resultado = resultado ? `${resultado} (${descripcion})` : descripcion;
    }

    if (incluirNombre) {
      resultado = resultado ? `${resultado} - ${nombre}` : nombre;
    }

    return resultado || '';
  }

  // REPORTE ANUAL
  openReportDialog() {
    this.showReportDialog = true;
    this.selectedYearReport = (new Date().getFullYear() - 1).toString();
  }

  async generateAnnualReport() {
    if (!this.selectedYearReport || !this.churchId) return;

    this.generatingReport = true;
    this.spinnerService.show();

    try {
      // 1. Obtener la información de la iglesia
      const iglesia: any = this.dataService.dataChurch$ ? await firstValueFrom(this.dataService.dataChurch$.pipe(take(1))) : null;

      // 2. Obtener todos los miembros
      // Intentar obtener de memoria primero (DataService) o local (todosLosMiembros)
      // Ojo: todosLosMiembros se llena en ngOnInit con DataService.members$
      let allMembers = this.dataService.getCurrentMembers();

      // Si no hay miembros cargados (ej. Tesorero que no entra a Members, o DataService vacío), intentar cargar de memberService
      if (!allMembers || allMembers.length === 0) {
        try {
          allMembers = await firstValueFrom(this.memberService.loadAllMembersForChurch(this.churchId));
          // No necesitamos actualizar this.todosLosMiembros obligatoriamente si DataService no lo hace,
          // pero podemos usar la variable local para este reporte.
        } catch (e) {
          this.toastService.showError("No se pudieron cargar los miembros para generar el reporte.");
          return;
        }
      }

      if (!allMembers || allMembers.length === 0) {
        this.toastService.showWarning("No se encontraron miembros registrados en la iglesia.");
        return;
      }

      const reportData: any[] = [];
      let totalGeneral = 0;

      // 3. Iterar y buscar diezmos
      for (const member of allMembers) {
        if (!member.id) continue;

        try {
          // Reutilizamos getDiezmoSummaries
          const summaries = await firstValueFrom(this.memberService.getDiezmoSummaries(this.churchId, member.id));
          const yearSummary = summaries.find((s: any) => s.year === this.selectedYearReport);

          if (yearSummary && yearSummary.total > 0) {
            totalGeneral += yearSummary.total;
            reportData.push({
              nombreCompleto: `${member.nombres} ${member.apellidos}`,
              documento: member.numeroDocumento,
              total: yearSummary.total
            });
          }
        } catch (e) {
        }
      }

      if (reportData.length === 0) {
        this.toastService.showInfo(`No se encontraron diezmos para el año ${this.selectedYearReport}`);
        this.generatingReport = false;
        this.spinnerService.hide();
        return;
      }

      // Ordenar alfabéticamente
      reportData.sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto));

      // 4. Generar PDF
      const pdfData = {
        anio: this.selectedYearReport,
        iglesiaNombre: iglesia?.churchName || 'IGLESIA CRISTIANA PENTECOSTÉS DE ESPAÑA',
        totalGeneral: totalGeneral,
        miembros: reportData
      };

      await this.reportePdfService.generarListadoDiezmosAnual(pdfData);
      this.toastService.showSuccess('Reporte generado correctamente');
      this.showReportDialog = false;

    } catch (err) {
      this.toastService.showError('Error al generar el reporte');
    } finally {
      this.generatingReport = false;
      this.spinnerService.hide();
    }
  }

  // ==================== GESTIร N DE BANCOS ====================

  /** @deprecated Los datos financieros ahora se cargan desde DataService al login. */
  async loadFinancialConfig() { }

  /** @deprecated El listener de saldos de cuentas ahora está en DataService. */
  private listenSaldosCuentas(_churchId: string): void { }

  /** Suma de saldos de cuentas activas (Saldo Total con Bancos). */
  getSaldoTotal(): number {
    if (!this.manejarBancos) {
      return Object.values(this.saldosCuentas).reduce((sum, v) => sum + v, 0);
    }
    return (this.cuentasBancarias ?? [])
      .filter(c => c.activo)
      .reduce((sum, c) => sum + (this.saldosCuentas[c.id] ?? 0), 0);
  }

  /** Lista de cuentas activas con su saldo actual (para el hero breakdown) */
  get cuentasConSaldo(): Array<{ nombre: string; saldo: number; tipo: string }> {
    if (!this.manejarBancos) return [];
    return (this.cuentasBancarias ?? [])
      .filter(c => c.activo)
      .map(c => ({ nombre: c.nombre, saldo: this.getSaldoCuenta(c.id), tipo: c.tipo ?? 'efectivo' }));
  }

  /** Saldo de una cuenta específica */
  getSaldoCuenta(cuentaId: string): number {
    return this.saldosCuentas[cuentaId] ?? 0;
  }

  getEmptyBankAccount(): CuentaBancaria {
    return {
      id: '',
      nombre: '',
      tipo: 'efectivo',
      esDefault: false,
      activo: true,
      orden: 1
    };
  }

}
